import React, { useState } from "react";
import { S, STAGES } from "./theme";

const blank = (user) => ({
  societyName: "",
  city: "",
  prospectName: "",
  prospectMobile: "",
  prospectEmail: "",
  referredBySales: false,
  salesPersonName: "",
  salesPersonMobile: "",
  stage: "New Lead",
  subscriptionValue: "",
  onboardingFee: "",
  notes: "",
  assignedToUid: user?.uid || "",
  assignedToName: user?.name || user?.email || "",
});

// Mobile rule: 10 digits. A country code is also allowed in front
// (the demo site sends US numbers as +1 followed by 10 digits).
const cleanMobileInput = (v) => {
  const digits = v.replace(/\D/g, "");
  if (v.trim().startsWith("+")) return ("+" + digits).slice(0, 14); // +, code, 10 digits
  return digits.slice(0, 10); // plain number: stop at 10 digits
};
const isValidMobile = (v) => /^(\+\d{1,3})?\d{10}$/.test(v);

export default function LeadModal({ lead, user, salesUsers, onSave, onClose, onDelete }) {
  const [form, setForm] = useState(lead ? { ...blank(user), ...lead } : blank(user));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const userLabel = (u) => u.name || u.email;
  const matchedReferrer = (salesUsers || []).find((u) => userLabel(u) === form.salesPersonName);
  // A lead saved earlier with a typed-in name that is not in the list keeps showing.
  const hasTypedReferrer = !!form.salesPersonName && !matchedReferrer;

  const pickReferrer = (id) => {
    if (id === "__typed") return; // keep the name already saved
    const u = (salesUsers || []).find((x) => x.id === id);
    set("salesPersonName", u ? userLabel(u) : "");
    set("salesPersonMobile", u ? (u.mobile || "") : "");
  };

  const handleSave = async () => {
    if (!form.societyName.trim()) { setError("Society name is required."); return; }
    if (!form.assignedToUid) { setError("Please assign this lead to a rep."); return; }
    if (form.prospectMobile && !isValidMobile(form.prospectMobile)) {
      setError("Prospect mobile must be exactly 10 digits.");
      return;
    }
    if (form.referredBySales && !form.salesPersonName) {
      setError("Please pick the sales person who referred this lead.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        ...form,
        subscriptionValue: Number(form.subscriptionValue) || 0,
        onboardingFee: Number(form.onboardingFee) || 0,
      });
      onClose();
    } catch (e) {
      setError("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div style={S.title}>
            {lead ? "Edit Lead" : "New Lead"}
            {lead && lead.source === "Demo Link" && (
              <span style={{ marginLeft: "10px", fontSize: "11px", fontWeight: 600, color: "#8888a8", border: "1px solid #2e2e40", borderRadius: "6px", padding: "2px 8px" }}>
                From Demo Link
              </span>
            )}
          </div>
          <button style={S.btnSmall} onClick={onClose}>✕ Close</button>
        </div>

        {error && <div style={{ ...S.msg, borderColor: "#f87171", color: "#f87171" }}>{error}</div>}

        <div style={S.formGrid}>
          <div>
            <label style={S.label}>Society / Prospect Name *</label>
            <input style={S.input} value={form.societyName} onChange={(e) => set("societyName", e.target.value)} placeholder="e.g. Green Meadows Residency" />
          </div>
          <div>
            <label style={S.label}>City</label>
            <input style={S.input} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Hyderabad" />
          </div>
        </div>

        <div style={S.formGrid}>
          <div>
            <label style={S.label}>Prospect Mobile</label>
            <input
              style={S.input}
              type="tel"
              inputMode="numeric"
              maxLength={14}
              value={form.prospectMobile}
              onChange={(e) => set("prospectMobile", cleanMobileInput(e.target.value))}
              placeholder="10-digit number"
            />
          </div>
          <div>
            <label style={S.label}>Prospect Email</label>
            <input style={S.input} value={form.prospectEmail} onChange={(e) => set("prospectEmail", e.target.value)} placeholder="name@example.com" />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ ...S.label, display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              checked={!!form.referredBySales}
              onChange={(e) => {
                const checked = e.target.checked;
                set("referredBySales", checked);
                if (!checked) {
                  set("salesPersonName", "");
                  set("salesPersonMobile", "");
                }
              }}
            />
            Referred by a sales person
          </label>
          {form.referredBySales && (
            <div style={{ ...S.formGrid, marginTop: 8 }}>
              <div>
                <label style={S.label}>Sales Person</label>
                <select
                  style={S.select}
                  value={matchedReferrer ? matchedReferrer.id : hasTypedReferrer ? "__typed" : ""}
                  onChange={(e) => pickReferrer(e.target.value)}
                >
                  <option value="">— Select —</option>
                  {hasTypedReferrer && <option value="__typed">{form.salesPersonName} (entered earlier)</option>}
                  {(salesUsers || []).map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Sales Person Mobile</label>
                <input
                  style={S.input}
                  value={form.salesPersonMobile || (form.salesPersonName ? "No mobile saved" : "")}
                  readOnly
                  placeholder="Filled in automatically"
                />
              </div>
            </div>
          )}
        </div>

        <div style={S.formGrid}>
          <div>
            <label style={S.label}>Stage</label>
            <select style={S.select} value={form.stage} onChange={(e) => set("stage", e.target.value)}>
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Assigned Rep *</label>
            {salesUsers && salesUsers.length > 0 ? (
              <select
                style={S.select}
                value={form.assignedToUid}
                onChange={(e) => {
                  const rep = salesUsers.find((u) => u.id === e.target.value);
                  set("assignedToUid", e.target.value);
                  set("assignedToName", rep ? (rep.name || rep.email) : "");
                }}
              >
                <option value="">— Select —</option>
                {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            ) : (
              <input style={S.input} value={form.assignedToName} readOnly />
            )}
          </div>
        </div>

        {form.stage === "Lost" && (
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>Reason Lost</label>
            <input style={S.input} value={form.lostReason || ""} onChange={(e) => set("lostReason", e.target.value)} placeholder="e.g. Went with a competitor" />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Notes</label>
          <textarea style={S.textarea} rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any context worth remembering…" />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <div>
            {lead && onDelete && user?.role === "Owner" && (
              <button style={S.btnRed} onClick={() => onDelete(lead.id)}>Delete Lead</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btnGray} onClick={onClose}>Cancel</button>
            <button style={S.btnBlue} onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Lead"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
